import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Tag, Empty, Spin, message } from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  ShopOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { getUserInfo } from '../utils/tokenManager';
import {
  listOpenRequests,
  getMyBid,
  participateOpenRequest,
  getAgencyLabel,
  getAgencyNo,
  getAgencyCompanyDetail,
} from '../services/openEstimateApi';

// ── 표시용 헬퍼 ──────────────────────────────────────────────────
const formatDate = (value, withTime = false) => {
  if (!value) return '-';
  try {
    const d = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return String(value);
    const opts = { year: 'numeric', month: '2-digit', day: '2-digit' };
    if (withTime) {
      opts.hour = '2-digit';
      opts.minute = '2-digit';
    }
    return new Intl.DateTimeFormat('ko-KR', opts).format(d);
  } catch {
    return String(value);
  }
};

const formatPeriod = (content) => {
  const start = content?.시작일;
  const end = content?.종료일;
  if (!start && !end) return '-';
  return `${start ? formatDate(start) : '-'} ~ ${end ? formatDate(end) : '-'}`;
};

const formatParty = (content) => {
  const party = content?.party || {};
  const 성인 = Number(party.성인수 || 0);
  const 소아 = Number(party.소아수 || 0);
  const 유아 = Number(party.유아수 || 0);
  if (!성인 && !소아 && !유아) return '-';
  const parts = [];
  if (성인) parts.push(`성인 ${성인}`);
  if (소아) parts.push(`소아 ${소아}`);
  if (유아) parts.push(`유아 ${유아}`);
  return parts.join(' / ');
};

const BID_STATUS_LABEL = {
  won: '낙찰',
  lost: '유찰',
  submitted: '응찰중',
  bidding: '응찰중',
};

const getBidStatusLabel = (status) => BID_STATUS_LABEL[status] || '';

const getBidStatusColorClass = (label) => {
  if (label === '낙찰') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (label === '유찰') return 'bg-red-50 text-red-700 border-red-200';
  if (label === '응찰중') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

const findMyBidInRow = (row, companyNo) => {
  if (!companyNo || !Array.isArray(row?.bids)) return null;
  const mine = Number(companyNo);
  return row.bids.find((b) => Number(b.supplier_no) === mine) ?? null;
};

const MyBidStatusBadge = ({ bid }) => {
  if (!bid) return <span className="text-sm text-gray-400">-</span>;
  const label = getBidStatusLabel(bid.status) || '참여완료';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${getBidStatusColorClass(label)}`}
    >
      {label}
    </span>
  );
};

const formatPrice = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toLocaleString('ko-KR');
};

/** 문장 종결(. ! ? 。) 뒤에 줄바꿈 삽입 — 소수점(3.5) 등은 제외 */
const formatSentenceLineBreaks = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return text
    .replace(/([.!?。])(?!\d)(?!\s*\n)\s*/g, '$1\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

const getEstimateContentLines = (value) =>
  formatSentenceLineBreaks(value)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const toDisplayText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.filter(Boolean).join(' / ');
  return '';
};

const InfoItem = ({ icon, label, value, className = '' }) => (
  <div className={`flex items-start gap-2.5 min-w-0 ${className}`}>
    <span className="mt-0.5 flex-shrink-0 text-gray-400 text-sm">{icon}</span>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className="text-sm text-gray-900 break-words">{value || '-'}</div>
    </div>
  </div>
);

const formatBizNo = (raw) => {
  const d = String(raw ?? '').replace(/\D/g, '');
  if (d.length === 10) return d.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
  return d || '-';
};

const formatAgencyAddress = (detail) => {
  if (!detail) return '-';
  const zip = String(detail.우편번호 ?? '').trim();
  const base = String(detail.기본주소 ?? '').trim();
  const addrDetail = String(detail.상세주소 ?? '').trim();
  const parts = [zip && `(${zip})`, base, addrDetail].filter(Boolean);
  return parts.join(' ') || '-';
};

const AgencyDetailPanel = ({ detail, loading, error, fallbackName }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
        <Spin size="small" />
        대리점 정보를 불러오는 중…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-4 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        {fallbackName ? `${fallbackName} — 등록된 업체 정보가 없습니다.` : '등록된 업체 정보가 없습니다.'}
      </div>
    );
  }

  const displayName = String(detail.업체명 ?? fallbackName ?? '').trim() || '-';
  const logoUrl = String(detail.회사로고 ?? '').trim();
  const intro = String(detail.회사소개 ?? '').trim();

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="size-full object-contain p-1" />
          ) : (
            <ShopOutlined className="text-lg text-gray-300" />
          )}
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="text-base font-semibold text-gray-900">{displayName}</div>
          {detail.대표자 ? (
            <div className="mt-0.5 text-sm text-gray-500">대표 {detail.대표자}</div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InfoItem icon={<PhoneOutlined />} label="대표전화" value={detail.대표전화} />
        <InfoItem icon={<MailOutlined />} label="대표이메일" value={detail.대표이메일} />
        <InfoItem icon={<EnvironmentOutlined />} label="주소" value={formatAgencyAddress(detail)} className="sm:col-span-2" />
        <InfoItem icon={<IdcardOutlined />} label="사업자등록번호" value={formatBizNo(detail.사업자등록번호)} />
        <InfoItem icon={<SafetyCertificateOutlined />} label="통신판매업신고" value={detail.통신판매업신고번호} />
        <InfoItem icon={<SafetyCertificateOutlined />} label="관광사업자번호" value={detail.관광사업자번호} className="sm:col-span-2" />
      </div>

      {intro ? (
        <div>
          <div className="mb-1.5 text-xs font-medium text-gray-500">회사 소개</div>
          <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
            {intro}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SectionCard = ({ title, icon, children, extra }) => (
  <section className="rounded-xl border border-gray-100 bg-white overflow-hidden">
    <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50/80 border-b border-gray-100">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
        {icon && <span className="text-blue-500">{icon}</span>}
        {title}
      </div>
      {extra}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const MEAL_LABELS = ['조식', '중식', '석식'];
const MEAL_LABEL_COLORS = ['text-orange-700', 'text-emerald-700', 'text-blue-700'];

const getDaySubtitle = (day, idx) => {
  const dayNum = idx + 1;
  const raw = String(day?.day ?? '').trim();
  if (!raw) return null;

  // 숫자만("2") → 현재 일차와 같으면 배지·우측 라벨과 중복이므로 생략
  if (/^\d+$/.test(raw)) {
    if (Number(raw) === dayNum) return null;
    return `${Number(raw)}일차`;
  }

  // "2일차" 형식 → 현재 일차와 같으면 생략
  const dayMatch = raw.match(/^(\d+)\s*일차$/);
  if (dayMatch && Number(dayMatch[1]) === dayNum) return null;

  return raw;
};

const isHotelPlace = (place) => {
  const p = String(place || '').toLowerCase();
  return /호텔|숙박|hotel|resort|리조트/.test(p);
};

const isFlightPlace = (place) => {
  const p = String(place || '');
  return /공항|항공|airport|flight/i.test(p);
};

// 일정 미리보기(structuredItinerary) — 상품 상세 일정표 스타일
const ItineraryPreview = ({ itinerary }) => {
  if (!Array.isArray(itinerary) || itinerary.length === 0) {
    return <Empty description="등록된 일정이 없습니다." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div className="max-h-[min(480px,55vh)] space-y-6 overflow-y-auto pr-1">
      {itinerary.map((day, dIdx) => {
        const locations = Array.isArray(day?.locations) ? day.locations : [];
        const meals = Array.isArray(day?.meals) ? day.meals : [];
        const daySubtitle = getDaySubtitle(day, dIdx);

        return (
          <div key={dIdx} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="shrink-0 text-sm font-semibold text-gray-900">{dIdx + 1}일차</span>
                {daySubtitle && (
                  <span className="truncate text-sm text-gray-600">{daySubtitle}</span>
                )}
              </div>
            </div>

            <div className="p-4">
              {locations.length > 0 ? (
                <div className="relative space-y-6">
                  <div className="absolute bottom-2 left-[7px] top-2 z-0 w-px -translate-x-1/2 bg-gray-200" aria-hidden />
                  {locations.map((loc, li) => {
                    const place = loc?.place || '장소 미지정';
                    const isHotel = isHotelPlace(place);
                    const isFlight = isFlightPlace(place);
                    return (
                      <div key={li} className="flex gap-3">
                        <div className="flex w-3.5 shrink-0 justify-center pt-[6px]">
                          <span
                            className="relative z-10 size-2 shrink-0 rounded-full bg-gray-400 ring-2 ring-white"
                            aria-hidden
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div
                            className={`mb-2 text-sm font-semibold leading-snug ${
                              isHotel ? 'text-purple-800' : isFlight ? 'text-sky-800' : 'text-gray-900'
                            }`}
                          >
                            {place}
                          </div>

                          {Array.isArray(loc?.schedule) && loc.schedule.length > 0 ? (
                            <ul className="space-y-2.5">
                              {loc.schedule.map((sch, si) => {
                                const details = Array.isArray(sch?.details) ? sch.details.filter(Boolean) : [];
                                const hasMeta = sch?.time || sch?.flight;
                                if (!hasMeta && details.length === 0) return null;
                                return (
                                  <li key={si} className="flex gap-2.5">
                                    {hasMeta && (
                                      <div className="flex w-[76px] shrink-0 flex-col gap-1 pt-0.5">
                                        {sch?.time && (
                                          <span className="inline-flex items-center justify-center rounded-md bg-white px-1.5 py-0.5 text-[11px] font-mono font-medium text-gray-700 shadow-sm ring-1 ring-gray-100">
                                            {toDisplayText(sch.time)}
                                          </span>
                                        )}
                                        {sch?.flight && (
                                          <span className="inline-flex items-center justify-center rounded-md bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                            {toDisplayText(sch.flight)}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    <div className={`min-w-0 flex-1 space-y-1 ${!hasMeta ? 'pl-0' : ''}`}>
                                      {details.length > 0 ? (
                                        details.map((dt, di) => (
                                          <p key={di} className="text-[13px] leading-relaxed text-gray-700">
                                            {toDisplayText(dt)}
                                          </p>
                                        ))
                                      ) : (
                                        <p className="text-[13px] text-gray-500">세부 일정 없음</p>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="text-xs text-gray-400">상세 일정이 없습니다.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty description="장소 정보가 없습니다." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>

            {meals.length > 0 && (
              <div className="border-t border-gray-100 px-4 py-3">
                <div className="mb-2 text-xs font-semibold text-gray-700">식사 정보</div>
                <div className="grid grid-cols-3 gap-2">
                  {MEAL_LABELS.map((label, mi) => (
                    <div key={label} className="rounded-lg px-2.5 py-2">
                      <div className={`mb-0.5 text-[11px] font-bold ${MEAL_LABEL_COLORS[mi]}`}>{label}</div>
                      <div className="text-xs leading-snug text-gray-700">{meals[mi] || '미정'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const OpenEstimateRequests = () => {
  const navigate = useNavigate();
  const { companyNo } = getUserInfo() || {};
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 상세 modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [previewMode, setPreviewMode] = useState('structured'); // 'structured' | 'html'
  const [myBid, setMyBid] = useState(null);
  const [myBidLoading, setMyBidLoading] = useState(false);
  const [agencyDetail, setAgencyDetail] = useState(null);
  const [agencyDetailLoading, setAgencyDetailLoading] = useState(false);
  const [agencyDetailError, setAgencyDetailError] = useState(null);

  const [participating, setParticipating] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) {
        message.warning('로그인이 필요합니다. 다시 로그인해 주세요.');
        navigate('/login');
        return;
      }
      const list = await listOpenRequests();
      setRequests(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('공개 견적 요청 목록 조회 실패:', err);
      setError(err?.message || '공개 견적 요청 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = async (request) => {
    setSelectedRequest(request);
    setPreviewMode('structured');
    setDetailOpen(true);
    setMyBid(null);
    setAgencyDetail(null);
    setAgencyDetailError(null);
    setMyBidLoading(true);
    setAgencyDetailLoading(true);

    const agencyNo = getAgencyNo(request);
    const bidPromise = getMyBid(request.id).catch((err) => {
      console.error('내 응찰 조회 실패:', err);
      return null;
    });
    const agencyPromise = agencyNo
      ? getAgencyCompanyDetail(agencyNo).catch((err) => {
          console.error('대리점 업체 정보 조회 실패:', err);
          return { __error: err?.message || '대리점 정보를 불러오지 못했습니다.' };
        })
      : Promise.resolve(null);

    try {
      const [bid, agency] = await Promise.all([bidPromise, agencyPromise]);
      setMyBid(bid);
      if (agency && agency.__error) {
        setAgencyDetailError(agency.__error);
      } else {
        setAgencyDetail(agency);
      }
    } finally {
      setMyBidLoading(false);
      setAgencyDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailOpen(false);
    setSelectedRequest(null);
    setMyBid(null);
    setAgencyDetail(null);
    setAgencyDetailError(null);
  };

  const handleParticipate = async () => {
    if (!selectedRequest || participating) return;
    setParticipating(true);
    try {
      await participateOpenRequest(selectedRequest);
      message.success('상담관리로 이관되었습니다');
      setDetailOpen(false);
      navigate('/quotes');
    } catch (err) {
      console.error('참여 실패:', err);
      message.error(err?.message || '참여에 실패했습니다.');
    } finally {
      setParticipating(false);
    }
  };

  const columns = [
    { key: '견적명', label: '견적명', widthClass: 'w-[18rem]' },
    { key: '대리점', label: '대리점', widthClass: 'w-[10rem]' },
    { key: '여행지', label: '여행지', widthClass: 'w-[9rem]' },
    { key: '기간', label: '기간', widthClass: 'w-[13rem]' },
    { key: '인원', label: '인원', widthClass: 'w-[10rem]' },
    { key: '응찰수', label: '응찰수', widthClass: 'w-[6rem]' },
    { key: '내응찰', label: '내 응찰', widthClass: 'w-[7rem]' },
    { key: '등록일', label: '등록일', widthClass: 'w-[8rem]' },
  ];

  const content = selectedRequest?.content || {};
  const requester = selectedRequest?.requester || {};
  const price = content?.price || {};
  const alreadyParticipated = !!myBid;

  return (
    <div className="space-y-4">
      <div className="mb-0 flex items-center justify-end">
        <button
          type="button"
          onClick={fetchRequests}
          className="inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
        >
          새로고침
        </button>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-16 rounded-2xl bg-white">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      )}
      {!loading && error && (
        <div className="rounded-2xl bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div>
          <div className="rounded-2xl bg-white p-5">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  {columns.map((c) => (
                    <col key={c.key} className={c.widthClass} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        scope="col"
                        className="h-10 bg-gray-100 first:rounded-l-lg last:rounded-r-lg px-3 text-center text-[13px] font-medium text-gray-500 whitespace-nowrap"
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-16 text-sm text-gray-500">
                        등록된 공개 견적 요청이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    requests.map((row) => {
                      const c = row?.content || {};
                      const myBidInRow = findMyBidInRow(row, companyNo);
                      const participated = !!myBidInRow;
                      return (
                        <tr
                          key={row.id}
                          className={`group cursor-pointer transition-colors border-b border-gray-200 focus-within:bg-gray-50 ${
                            participated
                              ? 'bg-emerald-50/40 hover:bg-emerald-50/70'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => openDetailModal(row)}
                          title="상세 보기"
                        >
                          <td className="px-3 py-3 align-top text-sm text-gray-900 whitespace-nowrap">
                            <div className="flex min-w-0 items-center gap-2">
                              {participated && (
                                <CheckCircleOutlined
                                  className="shrink-0 text-emerald-600"
                                  title="참여한 견적"
                                />
                              )}
                              <span className="truncate">{row.title || '-'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{getAgencyLabel(row)}</td>
                          <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{c.destination || c.city_name || '-'}</td>
                          <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{formatPeriod(c)}</td>
                          <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{formatParty(c)}</td>
                          <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{Array.isArray(row.bids) ? row.bids.length : 0}</td>
                          <td className="px-3 py-3 align-top text-center text-sm whitespace-nowrap">
                            <MyBidStatusBadge bid={myBidInRow} />
                          </td>
                          <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{formatDate(row.created_at)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 상세 Modal */}
      <Modal
        title={null}
        open={detailOpen}
        onCancel={closeDetailModal}
        footer={null}
        width={1100}
        centered
        destroyOnClose
        styles={{
          body: { padding: 0, maxHeight: 'calc(100vh - 96px)', overflowY: 'auto' },
          content: { padding: 0, overflow: 'hidden', borderRadius: 16, maxWidth: 'calc(100vw - 32px)' },
        }}
      >
        {selectedRequest && (
          <div>
            {/* 헤더 요약 */}
            <div className="relative overflow-hidden border-b border-blue-100 bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-600 px-8 py-6 text-white">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" />
              <div className="absolute -bottom-10 right-16 h-24 w-24 rounded-full bg-white/5" />
              <div className="relative">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Tag color="blue" className="!m-0 !border-white/30 !bg-white/15 !text-white">
                    공개 견적
                  </Tag>
                  <Tag className="!m-0 !border-white/30 !bg-white/10 !text-white/90">
                    응찰 {Array.isArray(selectedRequest.bids) ? selectedRequest.bids.length : 0}건
                  </Tag>
                  {alreadyParticipated && (
                    <Tag className="!m-0 !border-emerald-300/40 !bg-emerald-500/20 !text-emerald-50">
                      ✓ 참여완료
                    </Tag>
                  )}
                </div>
                <h2 className="text-xl font-bold leading-snug pr-8">
                  {selectedRequest.title || '공개 견적 요청'}
                </h2>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-blue-50/95">
                  <span className="inline-flex items-center gap-1.5">
                    <EnvironmentOutlined />
                    {content.destination || content.city_name || '-'}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarOutlined />
                    {formatPeriod(content)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <TeamOutlined />
                    {formatParty(content)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-5 bg-gray-50/60 px-8 py-6">
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* 요청 정보 */}
                <SectionCard title="요청 정보" icon={<ShopOutlined />}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <InfoItem
                      icon={<ShopOutlined />}
                      label="대리점"
                      value={agencyDetail?.업체명 || getAgencyLabel(selectedRequest)}
                    />
                    <InfoItem icon={<EnvironmentOutlined />} label="여행지" value={content.destination || content.city_name} />
                    <InfoItem icon={<CalendarOutlined />} label="기간" value={formatPeriod(content)} />
                    <InfoItem icon={<TeamOutlined />} label="인원" value={formatParty(content)} />
                    <InfoItem icon={<UserOutlined />} label="담당자" value={content.담당자} />
                    <InfoItem icon={<UserOutlined />} label="요청자" value={requester.요청자명} />
                    <InfoItem icon={<PhoneOutlined />} label="연락처" value={requester.요청자휴대폰} />
                    <InfoItem icon={<MailOutlined />} label="이메일" value={requester.요청자이메일} />
                    <InfoItem icon={<CalendarOutlined />} label="등록일" value={formatDate(selectedRequest.created_at, true)} className="sm:col-span-2" />
                  </div>
                </SectionCard>

                <SectionCard title="대리점 정보" icon={<ShopOutlined />}>
                  <AgencyDetailPanel
                    detail={agencyDetail}
                    loading={agencyDetailLoading}
                    error={agencyDetailError}
                    fallbackName={getAgencyLabel(selectedRequest)}
                  />
                </SectionCard>
              </div>

              <div className="flex flex-col gap-5">
                {/* 견적 내용 */}
                <SectionCard title="견적 내용" icon={<FileTextOutlined />}>
                  {(formatPrice(price.성인판매가) || formatPrice(price.소아판매가) || formatPrice(price.유아판매가)) && (
                    <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2.5">
                      <div className="mb-1.5 text-xs font-medium text-amber-800/80">희망 판매가</div>
                      <div className="flex flex-wrap gap-2">
                        {formatPrice(price.성인판매가) && (
                          <span className="rounded-md bg-white px-2 py-1 text-sm text-gray-800 shadow-sm">
                            성인 <strong>{formatPrice(price.성인판매가)}</strong>원
                          </span>
                        )}
                        {formatPrice(price.소아판매가) && (
                          <span className="rounded-md bg-white px-2 py-1 text-sm text-gray-800 shadow-sm">
                            소아 <strong>{formatPrice(price.소아판매가)}</strong>원
                          </span>
                        )}
                        {formatPrice(price.유아판매가) && (
                          <span className="rounded-md bg-white px-2 py-1 text-sm text-gray-800 shadow-sm">
                            유아 <strong>{formatPrice(price.유아판매가)}</strong>원
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {content.견적내용 ? (
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 text-sm leading-relaxed text-gray-700">
                      <ul className="m-0 list-none space-y-1.5 p-0">
                        {getEstimateContentLines(content.견적내용).map((line, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-gray-400" aria-hidden>
                              ·
                            </span>
                            <span className="min-w-0 flex-1">{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <Empty description="등록된 견적 내용이 없습니다." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </SectionCard>
              </div>

              {/* 일정 미리보기 */}
              <SectionCard
                title="요청일정 미리보기"
                icon={<CalendarOutlined />}
                extra={
                  content.itineraryHtml ? (
                    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-xs">
                      <button
                        type="button"
                        onClick={() => setPreviewMode('structured')}
                        className={`rounded-md px-2.5 py-1 transition-colors ${
                          previewMode === 'structured'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        기본
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMode('html')}
                        className={`rounded-md px-2.5 py-1 transition-colors ${
                          previewMode === 'html'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        HTML
                      </button>
                    </div>
                  ) : null
                }
              >
                {previewMode === 'html' && content.itineraryHtml ? (
                  <iframe
                    title="일정표 HTML 미리보기"
                    sandbox=""
                    srcDoc={content.itineraryHtml}
                    className="h-72 w-full rounded-lg border border-gray-200 bg-white"
                  />
                ) : (
                  <ItineraryPreview itinerary={content.itinerary} />
                )}
              </SectionCard>
            </div>

            {/* 하단 액션 */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-white px-8 py-4">
              <Button onClick={closeDetailModal}>닫기</Button>
              {myBidLoading ? (
                <Spin size="small" />
              ) : alreadyParticipated ? (
                <Button type="primary" disabled icon={<CheckCircleOutlined />}>
                  참여완료
                </Button>
              ) : (
                <Button type="primary" size="middle" loading={participating} onClick={handleParticipate}>
                  견적 참여하기
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OpenEstimateRequests;
